import { useState } from 'react';

import { useIdentity } from '@zenbusiness/application-commons-identity';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import DismissBanner from '@zenbusiness/zui/DismissBanner';
import Link from '@zenbusiness/zui/Link';

import { createLocalStorageKey } from '#client/constants/storage';
import { useEmailVerification } from '#client/hooks/useEmailVerification';
import useSendVerificationEmail from '#client/hooks/useSendVerificationEmail';

import { EmailIcon, SubtitleDetails } from './EmailVerificationBanner.style';

const MESSAGE_KEY = 'dashboard.component.emailVerificationBanner';
const MESSAGE_IDS = {
  title: `${MESSAGE_KEY}.title`,
  subtitle: `${MESSAGE_KEY}.subtitle`,
  subtitleHelp: `${MESSAGE_KEY}.subtitleHelp`,
  subtitleWithoutEmail: `${MESSAGE_KEY}.subtitleWithoutEmail`,
  resend: `${MESSAGE_KEY}.resend`,
  sent: `${MESSAGE_KEY}.sent`,
  // Supported error codes
  rateLimited: `${MESSAGE_KEY}.rateLimited`,
  alreadyVerified: `${MESSAGE_KEY}.alreadyVerified`,
  federatedIdentity: `${MESSAGE_KEY}.federatedIdentity`,
  // Fallback error code
  failed: `${MESSAGE_KEY}.failed`,
};

const STORAGE_KEY_PREFIX = createLocalStorageKey('evb_dismissed');

// Set of supported errors, each is used to show a message.
const SUPPORTED_ERROR_CODES = ['rateLimited', 'alreadyVerified', 'federatedIdentity'];

function EmailVerificationBanner() {
  const { formatMessage: fm } = useIntl();
  const { userUuid } = useIdentity() || {};
  const { email, shouldShowBanner } = useEmailVerification();
  const { sendVerificationEmail, loading } = useSendVerificationEmail();
  const [status, setStatus] = useState('idle');

  if (!shouldShowBanner) {
    return null;
  }

  const handleResend = async () => {
    const result = await sendVerificationEmail();
    if (result?.sent) {
      setStatus('sent');
      return;
    }
    if (SUPPORTED_ERROR_CODES.includes(result?.errorCode)) {
      setStatus(result?.errorCode);
      return;
    }
    setStatus('failed');
  };

  const subtitle = (() => {
    if (status === 'sent') {
      return fm({ id: MESSAGE_IDS.sent });
    }
    if (SUPPORTED_ERROR_CODES.includes(status)) {
      return fm({ id: MESSAGE_IDS[status] });
    }
    const isFailed = status === 'failed';
    const intro = email ? fm({ id: MESSAGE_IDS.subtitle }, { email }) : fm({ id: MESSAGE_IDS.subtitleWithoutEmail });
    return (
      <>
        {isFailed ? fm({ id: MESSAGE_IDS.failed }) : intro}
        <SubtitleDetails>
          <FormattedMessage
            id={MESSAGE_IDS.subtitleHelp}
            values={{
              resend: (
                <Link
                  clickstreamProps={{ elementName: 'resend_verification_email' }}
                  component="button"
                  disabled={loading}
                  onClick={handleResend}
                  type="button"
                >
                  {fm({ id: MESSAGE_IDS.resend })}
                </Link>
              ),
            }}
          />
        </SubtitleDetails>
      </>
    );
  })();

  return (
    <DismissBanner
      clickstreamProps={{ appSection: 'home', elementName: 'email_verification_banner' }}
      slots={{ startIcon: <EmailIcon /> }}
      storageKey={`${STORAGE_KEY_PREFIX}/${userUuid}`}
      subtitle={subtitle}
      title={fm({ id: MESSAGE_IDS.title })}
      variant="info"
    />
  );
}

export default EmailVerificationBanner;
